import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import CustomerList from '../components/customers/CustomerList';
import CustomerDetails from '../components/customers/CustomerDetails';
import CustomerForm from '../components/customers/CustomerForm';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import type { Customer } from '../types/customer';

const Customers: React.FC = () => {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const { showToast } = useToast();

  useEffect(() => {
    const state = location.state as { openNewCustomer?: boolean };
    if (state?.openNewCustomer) {
      setIsFormOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    loadCustomers();
    subscribeToCustomers();
  }, []);

  const subscribeToCustomers = () => {
    const subscription = supabase
      .channel('customers_channel')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'customers'
      }, (payload) => {
        loadCustomers();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  };

  const loadCustomers = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      showToast('Erro ao carregar clientes', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerSubmit = async (data: Customer) => {
    try {
      setIsLoading(true);

      const customerData = {
        name: data.name,
        type: data.type,
        document: data.document,
        email: data.email,
        phone: data.phone,
        whatsapp: data.whatsapp,
        status: data.status,
        address_street: data.address_street,
        address_number: data.address_number,
        address_complement: data.address_complement,
        address_neighborhood: data.address_neighborhood,
        address_city: data.address_city,
        address_state: data.address_state,
        address_zip: data.address_zip,
        notes: data.notes
      };

      if (editingCustomer) {
        const { error } = await supabase
          .from('customers')
          .update(customerData)
          .eq('id', editingCustomer.id);

        if (error) throw error;
        showToast('Cliente atualizado com sucesso!', 'success');
      } else {
        const { error } = await supabase
          .from('customers')
          .insert([customerData]);

        if (error) throw error;
        showToast('Cliente cadastrado com sucesso!', 'success');
      }

      setIsFormOpen(false);
      setEditingCustomer(null);
      await loadCustomers();
    } catch (error) {
      console.error('Error saving customer:', error);
      showToast(
        error instanceof Error ? error.message : 'Erro ao salvar cliente',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCustomer) return;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('customers')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', selectedCustomer.id);

      if (error) throw error;

      showToast('Cliente excluído com sucesso!', 'success');
      setSelectedCustomer(null);
      await loadCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      showToast(
        error instanceof Error ? error.message : 'Erro ao excluir cliente',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectCustomer = (customer: Customer | null) => {
    if (customer === null) {
      setIsFormOpen(true);
      setEditingCustomer(null);
      setSelectedCustomer(null);
    } else {
      setSelectedCustomer(customer);
      setIsFormOpen(false);
    }
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setIsFormOpen(true);
  };

  const handleBlock = async () => {
    if (!selectedCustomer) return;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('customers')
        .update({ status: 'blocked' })
        .eq('id', selectedCustomer.id);

      if (error) throw error;

      showToast('Cliente bloqueado com sucesso!', 'success');
      await loadCustomers();
    } catch (error) {
      console.error('Error blocking customer:', error);
      showToast('Erro ao bloquear cliente', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Gestão de Clientes
          </h1>
          <button
            onClick={() => handleSelectCustomer(null)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Novo Cliente
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <CustomerList
              customers={customers}
              onSelect={handleSelectCustomer}
              isLoading={isLoading}
            />
          </div>

          <div>
            {isFormOpen ? (
              <CustomerForm
                initialData={editingCustomer || undefined}
                onSubmit={handleCustomerSubmit}
                onCancel={() => {
                  setIsFormOpen(false);
                  setEditingCustomer(null);
                }}
                isLoading={isLoading}
              />
            ) : selectedCustomer ? (
              <CustomerDetails
                customer={selectedCustomer}
                purchases={[]}
                onEdit={() => handleEdit(selectedCustomer)}
                onBlock={handleBlock}
                onDelete={handleDelete}
                onSendMessage={() => {}}
              />
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                Selecione um cliente para ver os detalhes
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers;